import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Linkedin,
  Mail,
  MessageCircle,
  Link2,
  Smartphone,
  Globe,
} from 'lucide-react';
import {
  SUPPORT_CHANNEL_LABELS,
  SUPPORT_CHANNELS,
  SupportChannelKey,
} from '../../Types/SupportType';

const channelIcon: Record<SupportChannelKey, React.ElementType> = {
  linkedin: Linkedin,
  whatsapp: MessageCircle,
  email: Mail,
  telegram: Smartphone,
  direct: Globe,
};

export const channelPath = (channel: SupportChannelKey) =>
  `/admin/support/channels/${channel}`;

export const LINKS_TAB_PATH = '/admin/support/links';

export const OVERVIEW_PATH = '/admin/support/overview';

/**
 * Tabs for the Business Support detail page.
 *
 * Generate Link leads and is where a new campaign starts, so it is the first tab and the landing
 * route for `/admin/support` sub-pages. The five channel tabs follow in a fixed order rather than
 * being sorted by traffic: an admin who shares a link on LinkedIn expects to find LinkedIn in the
 * same place every time, and reordering tabs by volume would make them hunt for it.
 */
const SupportTabNav = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const tabs: { label: string; path: string; icon: React.ElementType }[] = [
    { label: 'Generate Link', path: LINKS_TAB_PATH, icon: Link2 },
    ...SUPPORT_CHANNELS.map((channel) => ({
      label: SUPPORT_CHANNEL_LABELS[channel],
      path: channelPath(channel),
      icon: channelIcon[channel],
    })),
  ];

  return (
    <div className="mb-6 flex flex-wrap gap-2 rounded-2xl border border-slate-800 bg-slate-900/60 p-2">
      {tabs.map((tab) => {
        const active = location.pathname === tab.path;
        const Icon = tab.icon;
        return (
          <button
            key={tab.path}
            onClick={() => navigate(tab.path)}
            className={`inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-black uppercase tracking-wider transition ${
              active
                ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-950 shadow-lg shadow-cyan-500/20'
                : 'border border-slate-800 bg-slate-950/60 text-slate-400 hover:border-cyan-500/40 hover:text-cyan-400'
            }`}
          >
            <Icon size={14} />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

export default SupportTabNav;
